<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user'
import { userBetsAPI } from '@/services/api'
import Currency from '@/components/Currency.vue'

import MyBetsList from './MyBets/List.vue'
import MyBoxes from './MyBets/MyBoxes.vue'
import Loading from '@/components/Loading.vue'
import PageHeader from '@/components/PageHeader.vue'

const { t } = useI18n()
const userStore = useUserStore()

// Reactive data
const bets = ref([])
const loading = ref(true)
const statusFilter = ref(null)
const searchQuery = ref('')

// Filtri
const statusOptions = computed(() => [
  { title: t('myBets.status.pending'), value: 'pending' },
  { title: t('myBets.status.won'), value: 'won' },
  { title: t('myBets.status.lost'), value: 'lost' },
  { title: t('myBets.status.void'), value: 'void' }
])





// Methods
const loadMyBets = async () => {
  loading.value = true
  try {
    const response = await userBetsAPI.getMyBets()
    if (response.success) {
      bets.value = response.data
      userStore.updateMyBets(response.data)
    }
  } catch (error) {
    console.error(t('messages.error.loadingBetsError'), error)
  } finally {
    loading.value = false
  }
}

const getBalanceColor = () => {
  const balance = userStore.user.balance
  if (balance > 0) return 'text-success'
  if (balance < 0) return 'text-amber'
  return 'text-primary'
}


// Lifecycle
onMounted(() => {
  loadMyBets()
})
</script>


<template>
  <v-container class="my-bets-container" v-if="userStore.isAuthenticated">
    <!-- Loading state -->
    <Loading v-if="loading" :message="$t('myBets.loading')" />

    <!-- Main content - only show when not loading -->
    <v-fade-transition>
      <div v-if="!loading">
        <PageHeader :title="$t('myBets.title')" icon="mdi-finance" />

        <!-- User balance here -->
        <v-row class="mb-6">
          <v-col cols="12" md="4">
            <v-card class="pa-4 text-center" elevation="2">
              <div class="text-h6 font-weight-medium mb-2">{{ $t('myBets.currentBalance') }}</div>
              <div class="text-h4 font-weight-bold" :class="getBalanceColor()">
                <Currency :amount="userStore.user.balance" />
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="8" v-if="userStore.user.balance === 0">
            <v-alert type="info" variant="outlined">
              {{ $t('myBets.zeroBalanceMessage') }}
              
            </v-alert>
          </v-col>
        </v-row>

        <!-- Statistiche rapide -->
        <!-- da fixare: deve contare tutto, non solo le scommesse caricate -->
        <!-- <MyBoxes :bets="bets" /> -->

        <!-- Filtri -->
        <v-row class="mb-4">
          <v-col cols="12" md="6">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              :label="$t('myBets.filters.filterByStatus')"
              variant="outlined"
              clearable
            >
            </v-select>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="searchQuery"
              :label="$t('myBets.filters.searchEvent')"
              variant="outlined"
              prepend-inner-icon="mdi-magnify"
              clearable
            >
            </v-text-field>
          </v-col>
        </v-row>

        <!-- Lista scommesse -->
        <MyBetsList 
          :bets="bets"
          :loading="false"
          :status-filter="statusFilter"
          :search-query="searchQuery"
        />
      
      </div>
    </v-fade-transition>
  </v-container>
</template>


<style scoped>
.my-bets-container {
  max-width: 1200px;
  margin: 0 auto;
}

.v-card {
  transition: transform 0.2s ease-in-out;
}

.v-card:hover {
  transform: translateY(-2px);
}
</style>